import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
} from "@mui/material";
import { useUser } from "./UserContext.jsx";
import { registerUser } from "../api/agentApi";
import { Email } from "@mui/icons-material";

const passwordHint =
  "Password must be at least 8 characters and contain at least 1 uppercase letter, 1 numeral, and 1 special character (!@#$%^&*).";
const specialChars = "!@#$%^&*";

function validatePassword(password: string) {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    new RegExp(`[${specialChars.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`).test(password)
  );
}

const AddUserPage: React.FC = () => {
  const { user } = useUser();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [role, setRole] = useState("user");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user || (user.role !== "admin" && user.role !== "operator-admin")) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5">Access Denied</Typography>
        <Typography>You must be an admin or operator-admin to add users.</Typography>
      </Box>
    );
  }

  const handlePasswordChange = (val: string) => {
    setPassword(val);
    setPasswordValid(validatePassword(val));
    setPasswordsMatch(val === retypePassword);
  };

  function validateEmail(email: string) {
    // Simple regex for email validation
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }

  const handleEmailChange = (val: string) => {
    setEmail(val);
    if (emailTouched) {
      setEmailValid(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailValid(validateEmail(email));
  };

  const handleRetypePasswordChange = (val: string) => {
    setRetypePassword(val);
    setPasswordsMatch(password === val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!passwordsMatch) {
      setError("Passwords must match.");
      return;
    }
    if (!validatePassword(password)) {
      setError("Password does not meet requirements.");
      return;
    }
    setLoading(true);
    try {
      const res = await registerUser({ username, email, password, role });
      setSuccess("User registered successfully.");
      setUsername("");
      setEmail("");
      setPassword("");
      setRetypePassword("");
      setRole("user");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to register user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
      <Paper elevation={3} sx={{ p: 4, minWidth: 400, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={2} color="#1a237e">
          Add New User
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={e => handleEmailChange(e.target.value)}
            onBlur={handleEmailBlur}
            fullWidth
            required
            margin="normal"
            error={!emailValid && email.length > 0 && emailTouched}
            helperText={!emailValid && email.length > 0 && emailTouched ? "Please enter a valid email address" : ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={e => setRole(e.target.value)}
            fullWidth
            required
            margin="normal"
            SelectProps={{ native: true }}
            helperText="Select user role"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            {user?.role === "operator-admin" && (
              <option value="operator-admin">Operator Admin</option>
            )}
          </TextField>
          <TextField
            label="Type Password"
            type="password"
            value={password}
            onChange={e => handlePasswordChange(e.target.value)}
            fullWidth
            required
            margin="normal"
            helperText={passwordHint}
            error={!passwordValid && password.length > 0}
          />
          <TextField
            label="Re-type Password"
            type="password"
            value={retypePassword}
            onChange={e => handleRetypePasswordChange(e.target.value)}
            fullWidth
            required
            margin="normal"
            error={!passwordsMatch && retypePassword.length > 0}
            helperText={!passwordsMatch && retypePassword.length > 0 ? "Passwords must match" : ""}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3, borderRadius: 2, fontWeight: 600, textTransform: "none" }}
            disabled={loading}
          >
            {loading ? "Registering..." : "Add User"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default AddUserPage;
